// ====================================
// File: components/EditorSidebar.jsx (Updated with Hidden Contact Elements on Mobile)
// ====================================
import React from "react";
import Icons from "../Icons";
import HeaderEditor from "../HeaderEditor";
import SectionEditor from "../SectionEditor";
import { SECTION_TEMPLATES } from "../constants";
import {
  PREMIUM_TEMPLATE_OPTIONS,
  NAVIGATION_SECTIONS,
} from "../ExecutivePremiumTemplate";

const EditorSidebar = ({
  resumeData,
  updateData,
  activePanel,
  setActivePanel,
  selectedTemplate,
  handleTemplateChange,
  showTemplateDropdown,
  setShowTemplateDropdown,
  templateDropdownRef,
  openFullscreen,
  calculateProgress,
  expandedSections,
  toggleSectionExpansion,
  showAddSectionMenu,
  setShowAddSectionMenu,
  dropdownRef,
  updateSection,
  addSection,
  removeSection,
  moveSection,
  addSectionItem,
  updateSectionItem,
  removeSectionItem,
  handleExportPDF,
  isExporting,
  exportError,
  isMobile,
  setMobileView,
}) => {
  const selectedTemplateData = PREMIUM_TEMPLATE_OPTIONS.find(
    (t) => t.id === selectedTemplate
  );

  // Calculate progress if not provided
  const getProgress = () => {
    if (typeof calculateProgress === "function") {
      return calculateProgress();
    }

    // Fallback calculation if function not provided
    let completed = 0;
    let total = 5;

    if (resumeData?.header?.name) completed++;
    if (resumeData?.header?.subtitle) completed++;
    if (resumeData?.header?.contactInfo?.length > 0) completed++;
    if (resumeData?.mainContent?.length > 0) completed++;
    if (resumeData?.sidebarContent?.length > 0) completed++;

    return Math.round((completed / total) * 100);
  };

  return (
    <div className="editor-sidebar">
      {/* Template Selector Header */}
      <div className="template-selector-header">
        <div className="editor-brand">
          <div className="editor-logo">
            <Icons.FileText className="w-6 h-6" />
          </div>
          <div>
            <h1 className="editor-title">Resume Builder</h1>
            <p className="editor-subtitle">Create your perfect resume</p>
          </div>
        </div>

        {/* Fullscreen Edit Button */}
        <button
          onClick={() => openFullscreen(activePanel)}
          className="fullscreen-edit-btn"
          title="Edit in fullscreen mode"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 16px",
            background: "rgba(255, 255, 255, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            borderRadius: "8px",
            color: "white",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s",
            marginTop: "16px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
        >
          <Icons.Maximize className="w-5 h-5" />
          <span>Fullscreen Edit</span>
        </button>

        {/* Preview Resume Button - Only on Mobile */}
        {isMobile && setMobileView && (
          <button
            onClick={() => setMobileView("preview")}
            className="preview-resume-btn"
            title="Preview your resume"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 16px",
              background: "rgba(255, 255, 255, 0.2)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s",
              marginTop: "8px",
              width: "100%",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
            }}
          >
            <Icons.Eye className="w-5 h-5" />
            <span>Preview Resume</span>
          </button>
        )}

        {/* Template Dropdown */}
        <div
          className="template-dropdown-container"
          ref={templateDropdownRef}
          style={{ marginTop: "20px" }}
        >
          <label className="template-dropdown-label">Choose Template</label>
          <button
            onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
            className={`template-dropdown-trigger ${
              showTemplateDropdown ? "active" : ""
            }`}
          >
            <div className="template-info">
              <div className="template-icon-wrapper">
                {selectedTemplateData?.icon}
              </div>
              <div className="template-text">
                <span className="template-name">
                  {selectedTemplateData?.name}
                </span>
                <span className="template-desc">
                  {selectedTemplateData?.description}
                </span>
              </div>
            </div>
            <Icons.ChevronDown className="dropdown-arrow w-5 h-5" />
          </button>

          {showTemplateDropdown && (
            <div className="template-dropdown-menu">
              {PREMIUM_TEMPLATE_OPTIONS.map((template) => (
                <div
                  key={template.id}
                  onClick={() => handleTemplateChange(template.id)}
                  className={`template-option ${
                    selectedTemplate === template.id ? "selected" : ""
                  }`}
                >
                  <div className={`template-preview ${template.preview}`} />
                  <div className="template-details">
                    <h4>{template.name}</h4>
                    <p>{template.description}</p>
                  </div>
                  {selectedTemplate === template.id && (
                    <span className="template-badge">Active</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Navigation - Hide on mobile */}
      {!isMobile && (
        <nav className="editor-navigation">
          <div className="nav-tabs">
            {NAVIGATION_SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => setActivePanel(section.id)}
                className={`nav-tab ${
                  activePanel === section.id ? "active" : ""
                }`}
              >
                <span className="nav-tab-icon">{section.icon}</span>
                <span className="nav-tab-label">{section.label}</span>
              </button>
            ))}
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${getProgress()}%` }}
            />
          </div>
        </nav>
      )}

      {/* Content Area */}
      {/* Hide the entire content area on mobile */}
      {!isMobile && (
        <div className="editor-content">
          {/* On mobile, default to main content if header is selected */}
          {activePanel === "header" && !isMobile && (
            <>
              <div className="quick-actions">
                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                  Complete your contact information to help employers reach you
                </span>
              </div>
              <HeaderEditor
                resumeData={resumeData}
                updateData={updateData}
                isMobile={isMobile} // Pass isMobile prop to HeaderEditor
              />
            </>
          )}

          {/* Show main/sidebar content - on mobile, always show main if header was selected */}
          {(activePanel === "main" ||
            activePanel === "sidebar" ||
            (isMobile && activePanel === "header")) && (
            <div style={{ position: "relative" }}>
              {/* Determine the effective panel for mobile */}
              {(() => {
                const effectivePanel =
                  isMobile && activePanel === "header" ? "main" : activePanel;
                return (
                  <>
                    <div className="quick-actions">
                      <button
                        onClick={() =>
                          setShowAddSectionMenu(!showAddSectionMenu)
                        }
                        className="quick-action-btn primary"
                      >
                        <Icons.Plus className="w-4 h-4" />
                        Add Section
                      </button>
                      {/* Hide helper text on mobile */}
                      {!isMobile && (
                        <span
                          style={{
                            fontSize: "14px",
                            color: "#6b7280",
                            marginLeft: "auto",
                          }}
                        >
                          {effectivePanel === "main"
                            ? "Add your work experience and achievements"
                            : "Add your skills, education, and certifications"}
                        </span>
                      )}
                    </div>

                    {/* Add Section Dropdown */}
                    {showAddSectionMenu && (
                      <div
                        className="template-dropdown-menu"
                        ref={dropdownRef}
                        style={{
                          position: "absolute",
                          width: isMobile ? "calc(100vw - 40px)" : "350px", // Responsive width on mobile
                          maxWidth: "350px",
                          top: "60px",
                          left: "0",
                          right: isMobile ? "0" : "auto", // Center on mobile
                          zIndex: 1000,
                          maxHeight: "400px",
                          overflowY: "auto",
                          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                          background: "white",
                        }}
                      >
                        <div
                          style={{
                            padding: "12px 16px",
                            margin: 0,
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            borderBottom: "1px solid #e5e7eb",
                            position: "sticky",
                            top: 0,
                            background: "white",
                            zIndex: 10,
                          }}
                        >
                          Choose a Section to Add:
                        </div>
                        <div style={{ padding: "8px" }}>
                          {Object.entries(
                            SECTION_TEMPLATES[
                              effectivePanel === "main" ? "main" : "sidebar"
                            ]
                          ).map(([key, template]) => (
                            <button
                              key={key}
                              onClick={() =>
                                addSection(
                                  effectivePanel === "main"
                                    ? "mainContent"
                                    : "sidebarContent",
                                  key
                                )
                              }
                              className="template-option"
                              style={{
                                padding: "12px",
                                width: "100%",
                                textAlign: "left",
                                marginBottom: "4px",
                                borderRadius: "6px",
                                border: "1px solid #e5e7eb",
                                background: "white",
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#f9fafb";
                                e.currentTarget.style.borderColor = "#667eea";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "white";
                                e.currentTarget.style.borderColor = "#e5e7eb";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                }}
                              >
                                <div
                                  className="template-icon-wrapper"
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    flexShrink: 0,
                                    background: "#f0f4ff",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#667eea",
                                  }}
                                >
                                  {template.icon}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <h4
                                    style={{
                                      fontSize: "14px",
                                      margin: "0 0 4px 0",
                                      fontWeight: 600,
                                      color: "#1a1a1a",
                                    }}
                                  >
                                    {template.title}
                                  </h4>
                                  <p
                                    style={{
                                      fontSize: "12px",
                                      margin: 0,
                                      color: "#6b7280",
                                      lineHeight: "1.4",
                                    }}
                                  >
                                    {template.description}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sections List */}
                    <div
                      className="sections-grid"
                      style={{ marginTop: "20px" }}
                    >
                      {resumeData[
                        effectivePanel === "main"
                          ? "mainContent"
                          : "sidebarContent"
                      ]?.length === 0 ? (
                        <div className="empty-state-card">
                          {effectivePanel === "main" ? (
                            <>
                              <Icons.Briefcase className="empty-state-icon" />
                              <h3 className="empty-state-title">
                                No Experience Added Yet
                              </h3>
                              <p className="empty-state-text">
                                Start building your resume by adding your work
                                experience, projects, or achievements.
                              </p>
                            </>
                          ) : (
                            <>
                              <Icons.GraduationCap className="empty-state-icon" />
                              <h3 className="empty-state-title">
                                No Skills or Education Added
                              </h3>
                              <p className="empty-state-text">
                                Add your skills, education, certifications, and
                                other qualifications.
                              </p>
                            </>
                          )}
                          <button
                            onClick={() => setShowAddSectionMenu(true)}
                            className="empty-state-action"
                          >
                            <Icons.Plus className="w-4 h-4" />
                            Add Your First Section
                          </button>
                        </div>
                      ) : (
                        resumeData[
                          effectivePanel === "main"
                            ? "mainContent"
                            : "sidebarContent"
                        ]?.map((section, index) => (
                          <div
                            key={section.id}
                            className={`section-card ${
                              expandedSections[section.id] !== false
                                ? "expanded"
                                : ""
                            }`}
                          >
                            <div className="section-card-header">
                              <div className="section-card-title">
                                {section.icon}
                                <span>{section.title}</span>
                              </div>
                              <div className="section-card-actions">
                                {index > 0 && (
                                  <button
                                    onClick={() =>
                                      moveSection(
                                        effectivePanel === "main"
                                          ? "mainContent"
                                          : "sidebarContent",
                                        index,
                                        "up"
                                      )
                                    }
                                    className="section-action-btn tooltip"
                                    data-tooltip="Move up"
                                  >
                                    <Icons.ChevronUp className="w-4 h-4" />
                                  </button>
                                )}
                                {index <
                                  resumeData[
                                    effectivePanel === "main"
                                      ? "mainContent"
                                      : "sidebarContent"
                                  ].length -
                                    1 && (
                                  <button
                                    onClick={() =>
                                      moveSection(
                                        effectivePanel === "main"
                                          ? "mainContent"
                                          : "sidebarContent",
                                        index,
                                        "down"
                                      )
                                    }
                                    className="section-action-btn tooltip"
                                    data-tooltip="Move down"
                                  >
                                    <Icons.ChevronDown className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    toggleSectionExpansion(section.id)
                                  }
                                  className="section-action-btn"
                                >
                                  {expandedSections[section.id] !== false ? (
                                    <Icons.ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <Icons.ChevronDown className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Remove "${section.title}" section?`
                                      )
                                    ) {
                                      removeSection(
                                        effectivePanel === "main"
                                          ? "mainContent"
                                          : "sidebarContent",
                                        section.id
                                      );
                                    }
                                  }}
                                  className="section-action-btn danger tooltip"
                                  data-tooltip="Remove section"
                                >
                                  <Icons.Trash className="w-4 h-4" />
                                </button>
                              </div>
                            </div>

                            {expandedSections[section.id] !== false && (
                              <SectionEditor
                                section={section}
                                location={
                                  effectivePanel === "main"
                                    ? "mainContent"
                                    : "sidebarContent"
                                }
                                index={index}
                                totalSections={
                                  resumeData[
                                    effectivePanel === "main"
                                      ? "mainContent"
                                      : "sidebarContent"
                                  ].length
                                }
                                updateSection={updateSection}
                                removeSection={removeSection}
                                moveSection={moveSection}
                                expandedSections={expandedSections}
                                toggleSectionExpansion={toggleSectionExpansion}
                                addSectionItem={addSectionItem}
                                updateSectionItem={updateSectionItem}
                                removeSectionItem={removeSectionItem}
                              />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* PDF Export Footer */}
      <div className="editor-footer">
        <button
          onClick={handleExportPDF}
          disabled={isExporting}
          className="export-pdf-btn"
        >
          {isExporting ? (
            <>
              <span className="spinner-icon">⟳</span>
              <span>Generating PDF...</span>
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              <span>Export PDF</span>
            </>
          )}
        </button>

        {exportError && (
          <div className="export-error">Error: {exportError}</div>
        )}
      </div>

      <style>{`
        .editor-footer {
          margin-top: auto;
          padding: 20px;
          background: linear-gradient(to bottom, #ffffff, #f8f9fa);
          border-top: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .export-pdf-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          max-width: 300px;
          padding: 14px 24px;
          background: linear-gradient(135deg, #065f46 0%, #047857 100%);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(6, 95, 70, 0.2);
        }

        .export-pdf-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #047857 0%, #065f46 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(6, 95, 70, 0.3);
        }

        .export-pdf-btn:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(6, 95, 70, 0.2);
        }

        .export-pdf-btn:disabled {
          background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
          box-shadow: none;
        }

        .export-pdf-btn svg {
          width: 20px;
          height: 20px;
        }

        .export-error {
          width: 100%;
          max-width: 300px;
          padding: 10px 14px;
          background-color: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          font-size: 14px;
          text-align: center;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Mobile-specific styles */
        @media (max-width: 768px) {
          .quick-actions {
            justify-content: center;
          }
          
          .nav-tabs {
            display: flex;
            justify-content: space-around;
            width: 100%;
          }
          
          .nav-tab {
            flex: 1;
            padding: 12px 8px;
          }
          
          .nav-tab-label {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

// Import PREMIUM_TEMPLATE_OPTIONS and NAVIGATION_SECTIONS from parent

// Add missing icons if not present
Icons.Eye =
  Icons.Eye ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  ));

Icons.Plus =
  Icons.Plus ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4v16m8-8H4"
      />
    </svg>
  ));

Icons.ChevronDown =
  Icons.ChevronDown ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  ));

Icons.ChevronUp =
  Icons.ChevronUp ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 15l7-7 7 7"
      />
    </svg>
  ));

Icons.Trash =
  Icons.Trash ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  ));

export default EditorSidebar;
